// EDEN · page pictures → one PDF, in the browser, with no library (the app has zero runtime dependencies).
//
// Teacher review, 15 Oct 2026 (point 7): "they can't download these pages even though we have the licence and the book
// is already on the web." The Book Reader's Download builds a PDF from the JPEG pictures the gate already serves:
// every page is one JPEG drawn full-page (DCTDecode — the picture's own bytes, never re-encoded), the page shaped like
// the picture, A4 wide. Pure functions; no DOM, so scripts/test-jpeg-pdf.mjs runs them under node.

export interface JpegInfo { width: number; height: number; components: number }

/** Width, height and colour channels from a JPEG's start-of-frame marker, or null for anything that is not a JPEG. */
export function jpegInfo(bytes: Uint8Array): JpegInfo | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let i = 2;
  while (i + 9 < bytes.length) {
    if (bytes[i] !== 0xff) { i++; continue; }
    const marker = bytes[i + 1]!;
    if (marker === 0xff) { i++; continue; } // fill byte
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) { i += 2; continue; } // no length
    const len = (bytes[i + 2]! << 8) | bytes[i + 3]!;
    // SOF0–SOF15, except DHT (C4), JPG (C8) and DAC (CC)
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      const height = (bytes[i + 5]! << 8) | bytes[i + 6]!;
      const width = (bytes[i + 7]! << 8) | bytes[i + 8]!;
      const components = bytes[i + 9]!;
      return width > 0 && height > 0 ? { width, height, components } : null;
    }
    if (marker === 0xda) return null; // start of scan before any frame header: not a usable JPEG
    i += 2 + len;
  }
  return null;
}

const enc = new TextEncoder();

/** One PDF holding each JPEG as a full page. Throws on a picture that is not a JPEG. */
export function jpegsToPdf(pictures: Uint8Array[], title = "EDEN pages"): Uint8Array {
  const chunks: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;
  const push = (part: Uint8Array | string): void => {
    const b = typeof part === "string" ? enc.encode(part) : part;
    chunks.push(b);
    length += b.length;
  };
  const object = (n: number, body: string | (Uint8Array | string)[]): void => {
    offsets[n] = length;
    push(`${n} 0 obj\n`);
    if (typeof body === "string") push(body);
    else for (const part of body) push(part);
    push("\nendobj\n");
  };
  push("%PDF-1.4\n%âãÏÓ\n"); // a comment of high bytes marks the file as binary
  const n = pictures.length;
  // objects: 1 catalog, 2 pages, 3 info, then 3 per page (page, contents, image)
  const pageObj = (i: number): number => 4 + i * 3;
  object(1, "<< /Type /Catalog /Pages 2 0 R >>");
  object(2, `<< /Type /Pages /Count ${n} /Kids [${pictures.map((_, i) => `${pageObj(i)} 0 R`).join(" ")}] >>`);
  object(3, `<< /Title (${title.replace(/[\\()]/g, (c) => `\\${c}`).replace(/[^\x20-\x7e]/g, "")}) /Producer (EDEN) >>`);
  pictures.forEach((jpeg, i) => {
    const info = jpegInfo(jpeg);
    if (!info) throw new Error(`Page ${i + 1} is not a JPEG picture.`);
    const w = 595.28; // A4 wide; the page takes the picture's own shape
    const h = Math.round((w * info.height) / info.width * 100) / 100;
    const space = info.components === 1 ? "/DeviceGray" : info.components === 4 ? "/DeviceCMYK /Decode [1 0 1 0 1 0 1 0]" : "/DeviceRGB";
    const draw = `q ${w} 0 0 ${h} 0 0 cm /Im${i} Do Q`;
    object(pageObj(i), `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${w} ${h}] /Resources << /XObject << /Im${i} ${pageObj(i) + 2} 0 R >> >> /Contents ${pageObj(i) + 1} 0 R >>`);
    object(pageObj(i) + 1, `<< /Length ${enc.encode(draw).length} >>\nstream\n${draw}\nendstream`);
    object(pageObj(i) + 2, [`<< /Type /XObject /Subtype /Image /Width ${info.width} /Height ${info.height} /ColorSpace ${space} /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`, jpeg, "\nendstream"]);
  });
  const xref = length;
  const count = 4 + n * 3;
  push(`xref\n0 ${count}\n0000000000 65535 f \n`);
  for (let k = 1; k < count; k++) push(`${String(offsets[k] ?? 0).padStart(10, "0")} 00000 n \n`);
  push(`trailer\n<< /Size ${count} /Root 1 0 R /Info 3 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  const out = new Uint8Array(length);
  let at = 0;
  for (const c of chunks) { out.set(c, at); at += c.length; }
  return out;
}
